import { Fab, Zoom, keyframes, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { DsIcon } from '@ice-web-app/designSystem';
import { useVoiceInputOptional } from './VoiceInputContext';
import { useWebSpeech } from './useWebSpeech';

// Pulsing animation for recording state
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

// Styled Floating Action Button
const StyledFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== 'isRecording',
})<{ isRecording: boolean }>(({ theme, isRecording }) => ({
  position: 'fixed',
  bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
  right: '20px',
  zIndex: 9999,
  animation: isRecording ? `${pulse} 1.5s ease-in-out infinite` : 'none',
  backgroundColor: isRecording ? theme.palette.error.main : theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: isRecording ? theme.palette.error.dark : theme.palette.primary.dark,
  },
  transition: 'background-color 0.3s ease',
  // Ensure visibility on tablet/iPad screens
  WebkitTransform: 'translateZ(0)',
  [theme.breakpoints.down('md')]: {
    bottom: 'calc(100px + env(safe-area-inset-bottom, 0px))',
    right: '16px',
  },
}));

export const VoiceInputButton: React.FC = () => {
  const voiceContext = useVoiceInputOptional();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleResult = (transcript: string) => {
    // Add a space before the transcript for natural sentence flow
    const textToInsert = `${transcript.trim()} `;
    if (voiceContext) {
      voiceContext.insertTextAtCaret(textToInsert);
    }
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
    if (voiceContext) {
      voiceContext.stopRecording();
    }
  };

  const { isSupported, start, stop } = useWebSpeech({
    onResult: handleResult,
    onError: handleError,
    lang: 'en-GB',
  });

  const isVisible = voiceContext?.isVisible ?? false;
  const isRecording = voiceContext?.isRecording ?? false;

  // Sync recording state with speech recognition
  useEffect(() => {
    if (voiceContext && isRecording) {
      start();
    } else {
      stop();
    }
  }, [isRecording, start, stop, voiceContext]);

  const handleClick = (e: React.MouseEvent) => {
    // Prevent the text field from losing focus when clicking the button
    e.preventDefault();

    if (!isSupported) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (errorMessage) {
      setErrorMessage(null);
    }

    if (voiceContext) {
      voiceContext.toggleRecording();
    }
  };

  // Don't render if context not available or browser not supported
  if (!voiceContext || !isSupported) {
    return null;
  }

  return (
    <Zoom in={isVisible} unmountOnExit>
      <StyledFab
        color="primary"
        aria-label="voice input"
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()} // Prevent focus loss from text field
        isRecording={isRecording}
        size="large"
      >
        <DsIcon icon={isRecording ? 'VOICE_OFF' : 'VOICE_ON'} />
      </StyledFab>
    </Zoom>
  );
};
